import React, { useState } from 'react';
import { Radio } from 'lucide-react';
import { RadioAdsContent } from '../types/ads.types';
import MediaUploader from '../components/MediaUploader';
import PlatformEditorTabs from '../components/PlatformEditorTabs';

interface BlobUrlManager {
  add: (url: string) => void;
  remove: (url: string) => void;
  cleanup: () => void;
}

interface RadioAdsEditorProps {
  content: RadioAdsContent;
  isEditing: boolean;
  onChange: (field: keyof RadioAdsContent, value: any) => void;
  blobManager?: BlobUrlManager;
  contentId?: number;
  apiBaseUrl?: string;
}

export default function RadioAdsEditor({ content, isEditing, onChange, blobManager, contentId, apiBaseUrl }: RadioAdsEditorProps) {
  const [currentScriptIndex, setCurrentScriptIndex] = useState(0);
  const [currentHeadlineIndex, setCurrentHeadlineIndex] = useState(0);
  const [showAllScripts, setShowAllScripts] = useState(false);
  const [showAllHeadlines, setShowAllHeadlines] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');
  const [activeAssetTab, setActiveAssetTab] = useState('audio');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    content.platforms || [content.platform || 'spotify']
  );

  const tabs = [
    { id: 'settings', label: 'Settings' },
    { id: 'assets', label: 'Assets' },
    { id: 'targeting', label: 'Targeting' },
    { id: 'budget', label: 'Budget' }
  ];

  // Initialize variations if they don't exist
  const scriptVariations = content.voiceoverScriptVariations || [content.voiceoverScript || ''];
  const headlineVariations = content.headlineVariations || [content.headline || ''];

  const updateScriptVariations = (variations: string[]) => {
    onChange('voiceoverScriptVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('voiceoverScript', variations[currentScriptIndex] || '');
  };

  const updateHeadlineVariations = (variations: string[]) => {
    onChange('headlineVariations', variations);
    // Keep main field in sync for backwards compatibility
    onChange('headline', variations[currentHeadlineIndex] || '');
  };

  const getPlatformName = () => {
    switch(content.platform) {
      case 'spotify': return 'Spotify';
      case 'pandora': return 'Pandora';
      case 'iheartradio': return 'iHeartRadio';
      case 'podcast': return 'Podcast Networks';
      case 'terrestrial_radio': return 'Terrestrial Radio';
      default: return 'Audio Platform';
    }
  };

  return (
    <div className="space-y-6">
      <PlatformEditorTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'settings' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Radio className="w-5 h-5 mr-2 text-orange-600" />
            Radio & Audio Campaign Settings
          </h3>

          <div className="space-y-4">
            {/* Campaign Name - Full Width */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Name</label>
              <input
                type="text"
                value={content.campaignName || ''}
                onChange={(e) => onChange('campaignName', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="Enter campaign name..."
              />
            </div>

            {/* Platforms - Checkboxes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audio Platforms</label>
              <div className="space-y-2">
                {[
                  { id: 'spotify', label: 'Spotify' },
                  { id: 'pandora', label: 'Pandora' },
                  { id: 'iheartradio', label: 'iHeartRadio' },
                  { id: 'podcast', label: 'Podcast Networks' },
                  { id: 'terrestrial_radio', label: 'Terrestrial Radio' },
                  { id: 'sirius_xm', label: 'SiriusXM' },
                  { id: 'amazon_music', label: 'Amazon Music' },
                  { id: 'youtube_music', label: 'YouTube Music' }
                ].map((platform) => (
                  <label key={platform.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedPlatforms.includes(platform.id)}
                      onChange={() => {
                        const newPlatforms = selectedPlatforms.includes(platform.id)
                          ? selectedPlatforms.filter(p => p !== platform.id)
                          : [...selectedPlatforms, platform.id];
                        setSelectedPlatforms(newPlatforms);
                        onChange('platforms', newPlatforms);
                      }}
                      disabled={!isEditing}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    <span className="ml-2 text-sm text-gray-700">{platform.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Spot Length */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Spot Length</label>
              <select
                value={content.spotLength || '30'}
                onChange={(e) => onChange('spotLength', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="15">15 seconds</option>
                <option value="30">30 seconds</option>
                <option value="60">60 seconds</option>
                <option value="120">2 minutes (Podcast)</option>
              </select>
            </div>

            {/* Voice Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Voice Type</label>
              <select
                value={content.voiceType || 'professional'}
                onChange={(e) => onChange('voiceType', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="professional">Professional Voice Over</option>
                <option value="ai_generated">AI Generated</option>
                <option value="celebrity">Celebrity Voice</option>
                <option value="host_read">Host Read (Podcast)</option>
                <option value="custom">Custom Recording</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'assets' && (
        <div className="space-y-6">
          {/* Asset Sub-tabs */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="border-b">
              <div className="flex space-x-1 p-1">
                <button
                  onClick={() => setActiveAssetTab('audio')}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeAssetTab === 'audio'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Audio
                </button>
                <button
                  onClick={() => setActiveAssetTab('images')}
                  className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeAssetTab === 'images'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Images
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Audio Files */}
              {activeAssetTab === 'audio' && (
                <div>
                  <MediaUploader
                    creatives={content.creatives?.filter(c => c.type === 'audio') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.creatives?.filter(c => c.type !== 'audio') || [];
                      onChange('creatives', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="Radio/Audio"
                    acceptedTypes={['audio/*']}
                    maxFiles={5}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />

                  <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <h4 className="text-sm font-medium text-orange-800 mb-2">Audio Requirements:</h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      <li>• Format: MP3, WAV, or AAC</li>
                      <li>• Duration: 15, 30, or 60 seconds</li>
                      <li>• Bitrate: 256kbps or higher</li>
                      <li>• Sample Rate: 44.1kHz or 48kHz</li>
                      <li>• Loudness: -16 to -14 LUFS (streaming)</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Companion Images */}
              {activeAssetTab === 'images' && (
                <div>
                  <MediaUploader
                    creatives={content.companionDisplayVariations?.filter(c => c.type === 'image') || []}
                    onCreativesChange={(creatives) => {
                      const otherCreatives = content.companionDisplayVariations?.filter(c => c.type !== 'image') || [];
                      onChange('companionDisplayVariations', [...otherCreatives, ...creatives]);
                    }}
                    disabled={!isEditing}
                    platform="Audio Companion"
                    acceptedTypes={['image/*']}
                    maxFiles={5}
                    blobManager={blobManager}
                    contentId={contentId}
                    apiBaseUrl={apiBaseUrl}
                  />

                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-sm font-medium text-green-800 mb-2">Companion Image Specs:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Spotify: 640x640 (square)</li>
                      <li>• Pandora: 500x500 (square)</li>
                      <li>• Podcast: 1400x1400 to 3000x3000</li>
                      <li>• Format: JPEG or PNG</li>
                      <li>• File Size: Under 1MB</li>
                      <li>• Include clear branding</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'targeting' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Audience Targeting</h3>
          <div className="space-y-4">
            {/* Demographics */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Age Range</label>
              <select
                value={content.ageRange || 'all'}
                onChange={(e) => onChange('ageRange', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all">All Ages</option>
                <option value="18-24">18-24</option>
                <option value="25-34">25-34</option>
                <option value="35-44">35-44</option>
                <option value="45-54">45-54</option>
                <option value="55-64">55-64</option>
                <option value="65+">65+</option>
              </select>
            </div>

            {/* Gender */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                value={content.gender || 'all'}
                onChange={(e) => onChange('gender', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="all">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            {/* Music Genres / Stations */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Music Genres / Station Formats</label>
              <input
                type="text"
                value={content.genres || ''}
                onChange={(e) => onChange('genres', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Pop, Rock, Country, Talk Radio, Sports"
              />
            </div>

            {/* Geographic Targeting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Geographic Markets</label>
              <input
                type="text"
                value={content.geoMarkets || ''}
                onChange={(e) => onChange('geoMarkets', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Los Angeles, New York, National, Top 50 DMAs"
              />
            </div>

            {/* Dayparting */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Daypart Targeting</label>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.dayparts?.includes('morning_drive') || false}
                    onChange={(e) => {
                      const parts = content.dayparts || [];
                      if (e.target.checked) {
                        onChange('dayparts', [...parts, 'morning_drive']);
                      } else {
                        onChange('dayparts', parts.filter(p => p !== 'morning_drive'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Morning Drive (6AM-10AM)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.dayparts?.includes('midday') || false}
                    onChange={(e) => {
                      const parts = content.dayparts || [];
                      if (e.target.checked) {
                        onChange('dayparts', [...parts, 'midday']);
                      } else {
                        onChange('dayparts', parts.filter(p => p !== 'midday'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Midday (10AM-3PM)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.dayparts?.includes('afternoon_drive') || false}
                    onChange={(e) => {
                      const parts = content.dayparts || [];
                      if (e.target.checked) {
                        onChange('dayparts', [...parts, 'afternoon_drive']);
                      } else {
                        onChange('dayparts', parts.filter(p => p !== 'afternoon_drive'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Afternoon Drive (3PM-7PM)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.dayparts?.includes('evening') || false}
                    onChange={(e) => {
                      const parts = content.dayparts || [];
                      if (e.target.checked) {
                        onChange('dayparts', [...parts, 'evening']);
                      } else {
                        onChange('dayparts', parts.filter(p => p !== 'evening'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Evening (7PM-12AM)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={content.dayparts?.includes('overnight') || false}
                    onChange={(e) => {
                      const parts = content.dayparts || [];
                      if (e.target.checked) {
                        onChange('dayparts', [...parts, 'overnight']);
                      } else {
                        onChange('dayparts', parts.filter(p => p !== 'overnight'));
                      }
                    }}
                    disabled={!isEditing}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Overnight (12AM-6AM)</span>
                </label>
              </div>
            </div>

            {/* Listener Interests */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Listener Interests</label>
              <input
                type="text"
                value={content.interests || ''}
                onChange={(e) => onChange('interests', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                placeholder="e.g., Fitness, Travel, Technology, Food, Business"
              />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'budget' && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Budget & Schedule</h3>
          <div className="space-y-4">
            {/* Budget Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pricing Model</label>
              <select
                value={content.pricingModel || 'cpm'}
                onChange={(e) => onChange('pricingModel', e.target.value)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              >
                <option value="cpm">CPM (Cost per 1000 Impressions)</option>
                <option value="spot">Cost per Spot</option>
                <option value="sponsorship">Sponsorship Package</option>
              </select>
            </div>

            {/* Campaign Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Budget ($)</label>
              <input
                type="number"
                value={content.budget || ''}
                onChange={(e) => onChange('budget', parseFloat(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="500"
                step="100"
                placeholder="Enter total budget"
              />
            </div>

            {/* Spot Rate / CPM */}
            {content.pricingModel === 'spot' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cost per Spot ($)</label>
                <input
                  type="number"
                  value={content.spotRate || ''}
                  onChange={(e) => onChange('spotRate', parseFloat(e.target.value) || 0)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  min="5"
                  step="5"
                  placeholder="e.g., $25-$500 per spot"
                />
              </div>
            ) : content.pricingModel === 'cpm' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Target CPM ($)</label>
                <input
                  type="number"
                  value={content.targetCPM || ''}
                  onChange={(e) => onChange('targetCPM', parseFloat(e.target.value) || 0)}
                  disabled={!isEditing}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  min="1"
                  step="1"
                  placeholder="Typical: $1-$25 CPM"
                />
              </div>
            ) : null}

            {/* Number of Spots */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Spots / Impressions</label>
              <input
                type="number"
                value={content.totalSpots || ''}
                onChange={(e) => onChange('totalSpots', parseInt(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="10"
                step="10"
                placeholder={content.pricingModel === 'cpm' ? 'Total impressions' : 'Number of spots'}
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weekly Frequency</label>
              <input
                type="number"
                value={content.weeklyFrequency || ''}
                onChange={(e) => onChange('weeklyFrequency', parseInt(e.target.value) || 0)}
                disabled={!isEditing}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                min="1"
                placeholder="Spots per week"
              />
            </div>

            {/* Campaign Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Campaign Schedule</label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={content.startDate || ''}
                    onChange={(e) => onChange('startDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">End Date</label>
                  <input
                    type="date"
                    value={content.endDate || ''}
                    onChange={(e) => onChange('endDate', e.target.value)}
                    disabled={!isEditing}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            {/* Estimated Reach */}
            {content.budget && (content.spotRate || content.targetCPM) && (
              <div className="mt-6 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="text-sm font-semibold text-orange-900 mb-2">Campaign Estimates</h4>
                {content.pricingModel === 'spot' && content.spotRate ? (
                  <>
                    <p className="text-2xl font-bold text-orange-700">
                      {Math.floor(content.budget / content.spotRate)} total spots
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      Based on ${content.budget} budget at ${content.spotRate}/spot
                    </p>
                    {content.weeklyFrequency && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-sm font-medium text-orange-800">Campaign Duration:</p>
                        <p className="text-lg font-semibold text-orange-700">
                          {Math.ceil(Math.floor(content.budget / content.spotRate) / content.weeklyFrequency)} weeks
                        </p>
                      </div>
                    )}
                  </>
                ) : content.pricingModel === 'cpm' && content.targetCPM ? (
                  <>
                    <p className="text-2xl font-bold text-orange-700">
                      {((content.budget / content.targetCPM) * 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} impressions
                    </p>
                    <p className="text-xs text-orange-600 mt-1">
                      Based on ${content.budget} budget at ${content.targetCPM} CPM
                    </p>
                    <div className="mt-3 pt-3 border-t border-orange-200">
                      <p className="text-sm font-medium text-orange-800">Estimated Reach:</p>
                      <p className="text-lg font-semibold text-orange-700">
                        {((content.budget / content.targetCPM) * 1000 / 3).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} unique listeners
                      </p>
                      <p className="text-xs text-orange-600">With average frequency of 3</p>
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}